import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import { api, AdminStats, RecentRegistration, SubscriptionInfo, DailyStats, ApiUsageStats, ApiAbuser, OverLimitResponse, ConversionsResponse, ConversionStats } from "@/lib/api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
} from "recharts";

const PLANS = ["free", "starter", "pro", "business", "enterprise"] as const;

function ConversionAnalytics() {
  const { data: stats } = useQuery<ConversionStats>({
    queryKey: ["admin-conversion-stats"],
    queryFn: () => api.admin.getConversionStats(30),
  });

  const { data: conversions } = useQuery<ConversionsResponse>({
    queryKey: ["admin-conversions"],
    queryFn: () => api.admin.getConversions({ limit: 20, days: 30 }),
  });

  const formatCurrency = (cents: number) => {
    const dollars = cents / 100;
    const sign = dollars >= 0 ? "+" : "";
    return `${sign}$${Math.abs(dollars).toFixed(0)}`;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Plan Conversions (Last 30 Days)</h2>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-emerald">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Upgrades</div>
              <div className="stat-value">{stats?.totals.upgrade?.count || 0}</div>
              <div className="text-sm text-emerald-600">
                {formatCurrency(stats?.totals.upgrade?.mrrChange || 0)}/mo
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-amber">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.51l-5.511-3.181" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Downgrades</div>
              <div className="stat-value">{stats?.totals.downgrade?.count || 0}</div>
              <div className="text-sm text-amber-600">
                {formatCurrency(stats?.totals.downgrade?.mrrChange || 0)}/mo
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-red">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Churned</div>
              <div className="stat-value">{stats?.totals.churn?.count || 0}</div>
              <div className="text-sm text-red-600">
                {formatCurrency(stats?.totals.churn?.mrrChange || 0)}/mo
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Tables */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Conversion Funnel */}
        <div className="card">
          <h3 className="text-lg font-semibold">Conversion Paths</h3>
          <p className="text-sm text-muted-foreground">Plan transitions</p>
          <div className="mt-4 space-y-2">
            {stats?.byTransition && stats.byTransition.length > 0 ? (
              stats.byTransition.slice(0, 8).map((t, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded bg-muted/50">
                  <div className="flex items-center gap-2">
                    <span className="badge badge-secondary text-xs">
                      {t.previousPlan}
                    </span>
                    <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                    <span className={`badge text-xs ${t.newPlan === 'free' ? 'badge-error' : 'badge-success'}`}>
                      {t.newPlan}
                    </span>
                  </div>
                  <span className="font-semibold">{t.count}</span>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No conversions yet
              </div>
            )}
          </div>
        </div>

        {/* Recent Conversions */}
        <div className="card">
          <h3 className="text-lg font-semibold">Recent Conversions</h3>
          <p className="text-sm text-muted-foreground">Latest plan changes</p>
          <div className="mt-4 max-h-64 overflow-y-auto">
            {conversions?.conversions && conversions.conversions.length > 0 ? (
              <table className="table">
                <thead className="sticky top-0 bg-white dark:bg-gray-900">
                  <tr className="table-header">
                    <th className="table-header-cell">Organization</th>
                    <th className="table-header-cell">Change</th>
                    <th className="table-header-cell text-right">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {conversions.conversions.map((c) => (
                    <tr key={c.id} className="table-row">
                      <td className="table-cell font-medium">{c.organization.name}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1 text-xs">
                          <span className="badge badge-secondary">{c.previousPlan}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className={`badge ${c.conversionType === 'upgrade' ? 'badge-success' : c.conversionType === 'churn' ? 'badge-error' : 'badge-warning'}`}>
                            {c.newPlan}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell text-right text-sm text-muted-foreground">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No conversions yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Trend Chart */}
      {stats?.daily && stats.daily.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold">Daily Conversion Trend</h3>
          <div className="mt-4 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.daily.map(d => ({
                ...d,
                date: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" fontSize={10} stroke="#9ca3af" />
                <YAxis fontSize={10} stroke="#9ca3af" allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="upgrades" fill="#10b981" name="Upgrades" stackId="a" />
                <Bar dataKey="downgrades" fill="#f59e0b" name="Downgrades" stackId="a" />
                <Bar dataKey="churns" fill="#ef4444" name="Churns" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanManagement() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<{
    id: string;
    name: string;
    plan: string;
  } | null>(null);
  const [selectedPlan, setSelectedPlan] = useState("");

  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ["admin-org-search", search],
    queryFn: () => api.admin.searchOrganizations(search),
    enabled: search.length >= 2,
  });

  const setPlanMutation = useMutation({
    mutationFn: ({ orgId, plan }: { orgId: string; plan: string }) =>
      api.admin.setOrganizationPlan(orgId, plan),
    onSuccess: (data) => {
      setSelectedOrg({
        id: data.organization.id,
        name: data.organization.name,
        plan: data.organization.plan,
      });
      setSelectedPlan("");
      queryClient.invalidateQueries({ queryKey: ["admin-org-search"] });
      queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
    },
  });

  return (
    <div className="card">
      <h2 className="text-lg font-semibold">Plan Management</h2>
      <p className="text-sm text-muted-foreground">
        Search for an organization and assign a plan
      </p>

      <div className="mt-4 space-y-4">
        {/* Search */}
        <div>
          <label htmlFor="org-search" className="block text-sm font-medium mb-2">
            Search Organizations
          </label>
          <input
            id="org-search"
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedOrg(null);
            }}
            placeholder="Search by organization name..."
            className="input input-primary"
          />
        </div>

        {/* Search Results */}
        {search.length >= 2 && (
          <div className="border rounded-lg max-h-48 overflow-y-auto">
            {searching ? (
              <div className="p-4 text-center text-muted-foreground">Searching...</div>
            ) : searchResults?.organizations && searchResults.organizations.length > 0 ? (
              <div className="divide-y">
                {searchResults.organizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => {
                      setSelectedOrg({ id: org.id, name: org.name, plan: org.plan });
                      setSearch("");
                    }}
                    className={`w-full px-4 py-3 text-left hover:bg-muted transition-colors ${
                      selectedOrg?.id === org.id ? "bg-muted" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{org.name}</div>
                        <div className="text-sm text-muted-foreground">{org.slug}</div>
                      </div>
                      <span className="badge badge-primary">
                        {org.plan.charAt(0).toUpperCase() + org.plan.slice(1)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-muted-foreground">No organizations found</div>
            )}
          </div>
        )}

        {/* Selected Organization */}
        {selectedOrg && (
          <div className="border rounded-lg p-4 bg-muted/50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-semibold">{selectedOrg.name}</div>
                <div className="text-sm text-muted-foreground">
                  Current plan: <span className="font-medium">{selectedOrg.plan}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrg(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex gap-3">
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="input input-primary flex-1"
              >
                <option value="">Select new plan...</option>
                {PLANS.filter((p) => p !== selectedOrg.plan).map((plan) => (
                  <option key={plan} value={plan}>
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (selectedPlan && selectedOrg) {
                    setPlanMutation.mutate({ orgId: selectedOrg.id, plan: selectedPlan });
                  }
                }}
                disabled={!selectedPlan || setPlanMutation.isPending}
                className="btn btn-primary"
              >
                {setPlanMutation.isPending ? "Updating..." : "Set Plan"}
              </button>
            </div>

            {setPlanMutation.isSuccess && (
              <div className="mt-3 text-sm text-emerald-600">
                Plan updated successfully!
              </div>
            )}
            {setPlanMutation.isError && (
              <div className="mt-3 text-sm text-red-600">
                Failed to update plan. Please try again.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminPage() {
  const { user } = useAuthStore();

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery<AdminStats>({
    queryKey: ["admin-stats"],
    queryFn: () => api.admin.getStats(),
  });

  const { data: registrations } = useQuery<RecentRegistration[]>({
    queryKey: ["admin-registrations"],
    queryFn: () => api.admin.getRegistrations(20),
  });

  const { data: subscriptions } = useQuery<SubscriptionInfo[]>({
    queryKey: ["admin-subscriptions"],
    queryFn: () => api.admin.getSubscriptions(),
  });

  const { data: dailyStats } = useQuery<DailyStats[]>({
    queryKey: ["admin-daily-stats"],
    queryFn: () => api.admin.getDailyStats(30),
  });

  const { data: apiUsage } = useQuery<ApiUsageStats>({
    queryKey: ["admin-api-usage"],
    queryFn: () => api.admin.getApiUsage(24),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: apiAbusers } = useQuery<{ period: string; threshold: number; abusers: ApiAbuser[] }>({
    queryKey: ["admin-api-abusers"],
    queryFn: () => api.admin.getApiAbusers(1, 30),
    refetchInterval: 60000,
  });

  const { data: overLimitData } = useQuery<OverLimitResponse>({
    queryKey: ["admin-over-limit"],
    queryFn: () => api.admin.getOverLimitOrganizations(),
    refetchInterval: 60000,
  });

  const { data: newsletterStats } = useQuery<{ total: number }>({
    queryKey: ["admin-newsletter-stats"],
    queryFn: () => api.admin.getNewsletterStats(),
  });

  if (statsError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-950">
        <h2 className="text-lg font-semibold text-red-800 dark:text-red-200">Access Denied</h2>
        <p className="mt-2 text-red-600 dark:text-red-400">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  const formattedDailyStats = dailyStats?.map((d) => ({
    ...d,
    date: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  }));

  const planData = stats?.organizations.byPlan
    ? Object.entries(stats.organizations.byPlan).map(([plan, count]) => ({
        plan: plan.charAt(0).toUpperCase() + plan.slice(1),
        count,
      }))
    : [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="relative z-10">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="mt-1 text-indigo-100">
            Business metrics and analytics for {user?.email}
          </p>
          <div className="mt-4 flex gap-3">
            <Link
              to="/admin/organizations"
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
              Organizations
            </Link>
            <Link
              to="/admin/users"
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
              Users
            </Link>
          </div>
        </div>
        <div className="page-header-decor" />
        <div className="page-header-decor-alt" />
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-primary">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Total Users</div>
              <div className="stat-value">
                {statsLoading ? "..." : stats?.users.total || 0}
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-emerald">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Organizations</div>
              <div className="stat-value">
                {statsLoading ? "..." : stats?.organizations.total || 0}
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-teal">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Domains</div>
              <div className="stat-value">
                {statsLoading ? "..." : (
                  <>
                    {stats?.domains.verified || 0}
                    <span className="text-sm text-muted-foreground">/{stats?.domains.total || 0}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-amber">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Reports</div>
              <div className="stat-value">
                {statsLoading ? "..." : stats?.reports.total || 0}
              </div>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-purple">
              <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div>
              <div className="stat-label">Newsletter</div>
              <div className="stat-value">
                {newsletterStats?.total || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Growth metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="card">
          <h3 className="text-lg font-semibold">User Growth</h3>
          <div className="mt-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Today</span>
              <span className="font-semibold text-emerald-600">+{stats?.users.today || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">This Week</span>
              <span className="font-semibold text-emerald-600">+{stats?.users.thisWeek || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">This Month</span>
              <span className="font-semibold text-emerald-600">+{stats?.users.thisMonth || 0}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold">Organizations by Plan</h3>
          <div className="mt-4 h-40">
            {planData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={planData} layout="vertical">
                  <XAxis type="number" fontSize={12} stroke="#9ca3af" />
                  <YAxis type="category" dataKey="plan" fontSize={12} stroke="#9ca3af" width={60} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-muted-foreground">
                No data yet
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold">Reports This Month</h3>
          <div className="mt-4 flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl font-bold text-indigo-600">
                {stats?.reports.thisMonth || 0}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">reports processed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration trend chart */}
      <div className="card">
        <h2 className="text-lg font-semibold">Daily Registrations (Last 30 Days)</h2>
        <div className="mt-4 h-64">
          {formattedDailyStats && formattedDailyStats.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedDailyStats}>
                <defs>
                  <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" fontSize={12} stroke="#9ca3af" />
                <YAxis fontSize={12} stroke="#9ca3af" allowDecimals={false} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#6366f1"
                  fillOpacity={1}
                  fill="url(#colorRegistrations)"
                  name="Registrations"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              No registration data yet
            </div>
          )}
        </div>
      </div>

      {/* Two column layout for tables */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Registrations */}
        <div className="card">
          <h2 className="text-lg font-semibold">Recent Registrations</h2>
          <p className="text-sm text-muted-foreground">Latest user sign-ups</p>
          <div className="mt-4 max-h-80 overflow-y-auto">
            {registrations && registrations.length > 0 ? (
              <table className="table">
                <thead className="sticky top-0 bg-white dark:bg-gray-900">
                  <tr className="table-header">
                    <th className="table-header-cell">User</th>
                    <th className="table-header-cell text-right">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg) => (
                    <tr key={reg.id} className="table-row">
                      <td className="table-cell">
                        <div className="font-medium">{reg.name}</div>
                        <div className="text-sm text-muted-foreground">{reg.email}</div>
                      </td>
                      <td className="table-cell text-right text-sm text-muted-foreground">
                        {new Date(reg.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No registrations yet
              </div>
            )}
          </div>
        </div>

        {/* Paid Subscriptions */}
        <div className="card">
          <h2 className="text-lg font-semibold">Paid Subscriptions</h2>
          <p className="text-sm text-muted-foreground">Organizations on paid plans</p>
          <div className="mt-4 max-h-80 overflow-y-auto">
            {subscriptions && subscriptions.length > 0 ? (
              <table className="table">
                <thead className="sticky top-0 bg-white dark:bg-gray-900">
                  <tr className="table-header">
                    <th className="table-header-cell">Organization</th>
                    <th className="table-header-cell">Plan</th>
                    <th className="table-header-cell text-right">Since</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="table-row">
                      <td className="table-cell font-medium">{sub.name}</td>
                      <td className="table-cell">
                        <span className="badge badge-primary">
                          {sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1)}
                        </span>
                      </td>
                      <td className="table-cell text-right text-sm text-muted-foreground">
                        {new Date(sub.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                No paid subscriptions yet
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Over Limit Organizations */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Organizations Over Limit</h2>
            <p className="text-sm text-muted-foreground">
              Customers who have exceeded their monthly message limit
            </p>
          </div>
          {(overLimitData?.count || 0) > 0 && (
            <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800 dark:bg-red-900/30 dark:text-red-400">
              {overLimitData?.count} over limit
            </span>
          )}
        </div>
        <div className="mt-4 max-h-80 overflow-y-auto">
          {overLimitData?.organizations && overLimitData.organizations.length > 0 ? (
            <table className="table">
              <thead className="sticky top-0 bg-white dark:bg-gray-900">
                <tr className="table-header">
                  <th className="table-header-cell">Organization</th>
                  <th className="table-header-cell">Plan</th>
                  <th className="table-header-cell text-right">Usage</th>
                  <th className="table-header-cell text-right">Days Over</th>
                  <th className="table-header-cell">Owner</th>
                </tr>
              </thead>
              <tbody>
                {overLimitData.organizations.map((org) => (
                  <tr key={org.id} className="table-row">
                    <td className="table-cell font-medium">{org.name}</td>
                    <td className="table-cell">
                      <span className="badge badge-primary">
                        {org.plan.charAt(0).toUpperCase() + org.plan.slice(1)}
                      </span>
                    </td>
                    <td className="table-cell text-right">
                      <div className="font-medium">
                        {org.monthlyMessageCount.toLocaleString()} / {org.messageLimit.toLocaleString()}
                      </div>
                      <div className={`text-xs ${org.percentUsed >= 120 ? 'text-red-600' : 'text-amber-600'}`}>
                        {org.percentUsed}%
                      </div>
                    </td>
                    <td className="table-cell text-right">
                      <span className={`font-semibold ${org.daysOverLimit >= 7 ? 'text-red-600' : 'text-amber-600'}`}>
                        {org.daysOverLimit}
                      </span>
                    </td>
                    <td className="table-cell text-sm text-muted-foreground">
                      {org.ownerEmail || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-8 text-center text-emerald-600">
              No organizations over limit
            </div>
          )}
        </div>
      </div>

      {/* Plan Management */}
      <PlanManagement />

      {/* Conversion Analytics */}
      <ConversionAnalytics />

      {/* API Usage Section */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold">API Usage (Tools)</h2>

        {/* API Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-icon stat-icon-primary">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Requests (24h)</div>
                <div className="stat-value">{apiUsage?.total || 0}</div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-icon stat-icon-emerald">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Avg Response</div>
                <div className="stat-value">{apiUsage?.avgResponseTimeMs || 0}ms</div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-icon stat-icon-teal">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Unique IPs (24h)</div>
                <div className="stat-value">{apiUsage?.byIp?.length || 0}</div>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-content">
              <div className={`stat-icon ${(apiAbusers?.abusers?.length || 0) > 0 ? 'stat-icon-red' : 'stat-icon-amber'}`}>
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Potential Abusers</div>
                <div className="stat-value">{apiAbusers?.abusers?.length || 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* API Usage Chart and Tables */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Hourly Usage Chart */}
          <div className="card">
            <h3 className="text-lg font-semibold">Hourly API Usage</h3>
            <p className="text-sm text-muted-foreground">Last 24 hours</p>
            <div className="mt-4 h-48">
              {apiUsage?.hourly && apiUsage.hourly.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={apiUsage.hourly.map(h => ({
                    ...h,
                    hour: new Date(h.hour).toLocaleTimeString("en-US", { hour: "numeric" }),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="hour" fontSize={10} stroke="#9ca3af" />
                    <YAxis fontSize={10} stroke="#9ca3af" allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={false} name="Requests" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  No API usage data yet
                </div>
              )}
            </div>
          </div>

          {/* Top Endpoints */}
          <div className="card">
            <h3 className="text-lg font-semibold">Top Endpoints</h3>
            <p className="text-sm text-muted-foreground">Most used endpoints (24h)</p>
            <div className="mt-4 max-h-48 overflow-y-auto">
              {apiUsage?.byEndpoint && apiUsage.byEndpoint.length > 0 ? (
                <table className="table">
                  <thead className="sticky top-0 bg-white dark:bg-gray-900">
                    <tr className="table-header">
                      <th className="table-header-cell">Endpoint</th>
                      <th className="table-header-cell text-right">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiUsage.byEndpoint.slice(0, 10).map((ep) => (
                      <tr key={ep.endpoint} className="table-row">
                        <td className="table-cell font-mono text-xs">{ep.endpoint}</td>
                        <td className="table-cell text-right">{ep.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No endpoint data yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top IPs and Potential Abusers */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Top IPs */}
          <div className="card">
            <h3 className="text-lg font-semibold">Top IPs by Volume</h3>
            <p className="text-sm text-muted-foreground">Highest request volume (24h)</p>
            <div className="mt-4 max-h-64 overflow-y-auto">
              {apiUsage?.byIp && apiUsage.byIp.length > 0 ? (
                <table className="table">
                  <thead className="sticky top-0 bg-white dark:bg-gray-900">
                    <tr className="table-header">
                      <th className="table-header-cell">IP Address</th>
                      <th className="table-header-cell text-right">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiUsage.byIp.map((ip) => (
                      <tr key={ip.ipAddress} className="table-row">
                        <td className="table-cell font-mono text-xs">{ip.ipAddress}</td>
                        <td className="table-cell text-right">{ip.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  No IP data yet
                </div>
              )}
            </div>
          </div>

          {/* Potential Abusers */}
          <div className="card">
            <h3 className="text-lg font-semibold">Potential Abusers</h3>
            <p className="text-sm text-muted-foreground">
              IPs with 30+ requests in the last hour
            </p>
            <div className="mt-4 max-h-64 overflow-y-auto">
              {apiAbusers?.abusers && apiAbusers.abusers.length > 0 ? (
                <table className="table">
                  <thead className="sticky top-0 bg-white dark:bg-gray-900">
                    <tr className="table-header">
                      <th className="table-header-cell">IP Address</th>
                      <th className="table-header-cell">Endpoints</th>
                      <th className="table-header-cell text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiAbusers.abusers.map((abuser) => (
                      <tr key={abuser.ipAddress} className="table-row">
                        <td className="table-cell font-mono text-xs">{abuser.ipAddress}</td>
                        <td className="table-cell text-xs text-muted-foreground">
                          {abuser.endpoints?.slice(0, 3).join(", ")}
                          {(abuser.endpoints?.length || 0) > 3 && "..."}
                        </td>
                        <td className="table-cell text-right font-semibold text-red-600">
                          {abuser.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-8 text-center text-emerald-600">
                  No abuse detected
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
